import type { AdminSupportTicketDTO } from '@practium/features/support';
import { MessageThread, SupportCardShell, TicketStatus } from '@practium/features/support';
import { Button } from '@practium/ui';
import { Hash, Sparkles } from 'lucide-react';
import { useState } from 'react';
import styles from './AdminSupportTicketCard.module.css';

interface AdminSupportTicketCardProps {
	ticket: AdminSupportTicketDTO;
	onReply: (ticketId: string, message: string) => Promise<void>;
}

const MAX_CHARS = 2000;

export const AdminSupportTicketCard = ({ ticket, onReply }: AdminSupportTicketCardProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [replyText, setReplyText] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const createdAt = ticket.createdAt ? new Date(ticket.createdAt) : new Date();
	const isOpen = ticket.status === TicketStatus.Open;
	const messages = ticket.messages ?? [];
	const firstMessage = messages[0];
	const lastMessage = messages[messages.length - 1];

	const handleResponder = async () => {
		if (replyText.trim().length < 10) return;
		setIsSubmitting(true);
		try {
			await onReply(ticket._id, replyText);
			setReplyText('');
			setIsExpanded(false);
		} finally {
			setIsSubmitting(false);
		}
	};

	const expandedContent = isOpen ? (
		<div className={styles.replyWrapper}>
			<div className={styles.replyArea}>
				<div className={styles.questionRow}>
					<div className={styles.questionBody}>
						<span className={styles.questionUser}>{ticket.user.name}</span>
						<div className={styles.questionTextWrapper}>
							<span className={styles.questionDot} />
							<p className={styles.questionText}>{firstMessage?.content}</p>
						</div>
					</div>
				</div>

				<div className={styles.badges}>
					<button type="button" className={styles.badgeQuick}>
						<Hash className={styles.badgeIcon} /> Respostas Rápidas
					</button>
					<button type="button" className={styles.badgeFlora}>
						<Sparkles className={styles.badgeIcon} /> Ajuda da Flora
					</button>
				</div>

				<div className={styles.textareaRow}>
					<div className={styles.textareaWrapper}>
						<textarea
							className={styles.textarea}
							placeholder="Escreva sua resposta..."
							value={replyText}
							onChange={(e) => setReplyText(e.target.value.slice(0, MAX_CHARS))}
							rows={10}
						/>
						<div className={styles.charCounter}>
							<span>
								{replyText.length} / {MAX_CHARS}
							</span>
						</div>
					</div>
					<Button
						variant="primary"
						onClick={handleResponder}
						disabled={replyText.trim().length < 10 || isSubmitting}
						className={styles.respondBtn}
					>
						{isSubmitting ? 'Enviando...' : 'Responder'}
					</Button>
				</div>

				<div className={styles.replyFooter}>
					<div className={styles.footerLeft}>
						<span className={styles.footerDot} />
						<span className={styles.footerText}>
							Crie respostas rápidas para responder perguntas sobre o mesmo tema.{' '}
							<button type="button" className={styles.footerLink}>
								Criar resposta rápida.
							</button>
						</span>
					</div>
				</div>
			</div>
		</div>
	) : (
		<MessageThread messages={messages} />
	);
	return (
		<SupportCardShell
			avatarUrl={ticket.user.avatar?.url}
			userName={ticket.user.name}
			subject={ticket.subject}
			createdAt={createdAt}
			isExpanded={isExpanded}
			onExpand={() => setIsExpanded((p) => !p)}
			timeAgoClassName={isOpen ? styles.timeAgoOpen : undefined}
			preview={!isOpen ? lastMessage?.content : undefined}
			expandedContent={expandedContent}
		/>
	);
};
